import { useQuery } from '@tanstack/react-query';

import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardAction, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { DetailList } from '@/components/shared/DetailList';
import { StatusPill } from '@/components/shared/StatusPill';
import { getMyTenant, TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';
import type { BcConnection } from '@/lib/api/connections';
import { formatDate, orDash } from '@/lib/format';
import bigCommerceLogo from '@/assets/shops/BigCommerce.svg?url';

import { SyncConfigSection } from './SyncConfigSection';

/**
 * Stores — the eCommerce platforms orders sync from. Read-only summary
 * of the connected store in MVP; connect / disconnect and per-platform
 * settings land in a follow-up. Off the `tenant/me` query.
 */
export function StoresPage() {
  const tenantQuery = useQuery({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });

  const tenant = tenantQuery.data;

  return (
    <div className="max-w-3xl">
      <PageHeader title="Stores" subtitle="The eCommerce platforms your orders sync from" />
      {tenant ? <StoreCard connection={tenant.bc_connection} /> : null}
    </div>
  );
}

function StoreCard({ connection }: { connection?: BcConnection }) {
  return (
    <Card>
      <CardHeader>
        <img src={bigCommerceLogo} alt="BigCommerce" className="mb-2 h-24 w-auto" />
        <CardDescription>Your connected BigCommerce store.</CardDescription>
        {connection ? <CardAction>{renderConnectionStatus(connection.status)}</CardAction> : null}
      </CardHeader>
      <CardContent>
        {connection ? (
          <DetailList
            items={[
              {
                label: 'Store hash',
                value: <span className="tabular-nums">{orDash(connection.bc_store_hash)}</span>,
              },
              { label: 'Connected as', value: orDash(connection.bc_user_email) },
              { label: 'Installed', value: formatDate(connection.installed_at) },
            ]}
          />
        ) : (
          <p className="text-muted-foreground text-sm">No store connected.</p>
        )}
      </CardContent>
      {connection?.status === 'active' ? (
        <SyncConfigSection connectionId={connection.connection_id} />
      ) : null}
    </Card>
  );
}

function renderConnectionStatus(status: BcConnection['status']) {
  if (status === 'active') return <StatusPill tone="success">Active</StatusPill>;
  if (status === 'pending') return <StatusPill tone="warning">Pending</StatusPill>;
  return <StatusPill tone="muted">Revoked</StatusPill>;
}
